/**
 * streamAiAsk — fetch-based NDJSON client for `POST /api/ai/ask/stream`.
 *
 * Calls into the new backend streaming endpoint and emits per-line
 * events via the `onEvent` callback. The endpoint's wire format is
 * one JSON object per line:
 *
 *     {"type": "step",   "step": 0, "tool": "...", "label": "..."}
 *     {"type": "result", "step": 0, "summary": {...}}
 *     {"type": "final",  "answer": "...", "results": [...], ...}
 *     {"type": "error",  "message": "..."}
 *
 * Why not `EventSource`? EventSource only supports GET requests and
 * doesn't let you set custom headers — which would force us to leak
 * the query into the URL. Plain `fetch()` + `ReadableStream` is
 * ~25 LOC and works identically.
 *
 * Why not Axios? Axios `responseType: 'stream'` does not work in
 * browsers — the streaming `ReadableStream` body is a fetch-only
 * feature. We keep using Axios for every other request; this is the
 * only place fetch is justified.
 *
 * Auth: relies on the same httpOnly session cookie our Axios client
 * uses (set during /api/auth/login). `credentials: 'include'`
 * carries it on cross-origin streaming requests too.
 */

const STREAM_PATH = '/api/ai/ask/stream';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sends a streaming AI-ask request. Each NDJSON line is parsed and
 * passed to `onEvent`. The promise resolves with the `final` event
 * payload (or rejects on HTTP error / abort).
 *
 * @param {object} opts
 * @param {string} opts.query         - User query (>= 2 chars).
 * @param {string|null} opts.sessionId - Conversation-memory id.
 * @param {AbortSignal} opts.signal   - Abort signal for cancellation.
 * @param {(event: object) => void} opts.onEvent - Per-line callback.
 * @returns {Promise<object>}         - Resolves with the final payload.
 */
export async function streamAiAsk({ query, sessionId, signal, onEvent }) {
  const resp = await fetch(`${BACKEND_URL}${STREAM_PATH}`, {
    method: 'POST',
    credentials: 'include',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, sessionId: sessionId || null }),
  });

  if (!resp.ok) {
    // Surface body if the server returned an error JSON (FastAPI's
    // HTTPException shape). Falls back to a generic message if the
    // body isn't readable for any reason.
    let detail = `HTTP ${resp.status}`;
    try {
      const body = await resp.json();
      detail = body?.detail || detail;
    } catch { /* non-JSON body — keep the generic message */ }
    throw new Error(detail);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder('utf-8');
  // Buffer accumulates partial lines across chunk boundaries. NDJSON
  // is line-delimited but a single TCP packet can split a line down
  // the middle — we hold the tail until the next chunk arrives.
  let buffer = '';
  let finalPayload = null;

  // The reader's `read()` loop. Each chunk is appended to the buffer,
  // then we drain every COMPLETE line (everything before the last `\n`)
  // and keep the trailing partial line for next iteration.
  while (true) {
    // `read()` resolves when the next chunk arrives OR the stream
    // ends (done=true). Aborting the signal causes the fetch to
    // reject; we let that propagate up — the caller's catch block
    // handles it.
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let newlineIdx;
    while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, newlineIdx).trim();
      buffer = buffer.slice(newlineIdx + 1);
      if (!line) continue;
      let event;
      try { event = JSON.parse(line); }
      catch { continue; /* skip malformed lines defensively */ }
      try { onEvent?.(event); } catch { /* listener errors don't kill the stream */ }
      if (event.type === 'final') finalPayload = event;
    }
  }

  // Flush any trailing buffered line (server should always end on a
  // newline, but be tolerant).
  if (buffer.trim()) {
    try {
      const event = JSON.parse(buffer.trim());
      onEvent?.(event);
      if (event.type === 'final') finalPayload = event;
    } catch { /* ignore */ }
  }

  if (!finalPayload) {
    throw new Error('Stream ended without a final event');
  }
  return finalPayload;
}
